import React from 'react';
import HighchartsReact from 'highcharts-react-official';
import PropTypes from 'prop-types';
import { Common } from '../../utils/common';

const Highcharts = window.Highcharts;

function CallAnalysis({ data, conversionRates, prospectRates, results, industry, currentYear }) {

  const { calltypes } = data;

  const totalCalls = results.at(-1).total_calls;

  const nonProspectCalls = calltypes.reduce((acc, calltype) => {
    if (calltype.is_prospect) {
      return acc + calltype.total_calls;
    } else {
      return acc;
    }
  }, 0);

  const callTypeMap = calltypes.reduce((acc, calltype) => {
    acc[calltype.text] = calltype;
    return acc;
  }, {});

  const missedOppSales = callTypeMap['Sales']?.noappt_calls ?? callTypeMap['Prospect']?.noappt_calls ?? 0;
  const missedOppService = callTypeMap['Service']?.noappt_calls ?? 0;

  const apptSales = callTypeMap['Sales']?.appt_calls ?? callTypeMap['Prospect']?.appt_calls ?? 0;
  const apptService = callTypeMap['Service']?.appt_calls ?? 0;

  const currentYearData = results.filter((result) => result.start_date.startsWith(currentYear));

  //const noAnswerCalls = currentYearData.reduce((acc, result) => {
  //  return acc + result.no_answer_calls;
  //}, 0);

  const noAnswerCalls = results.at(-1).no_answer_calls;

  //const busyCalls = currentYearData.reduce((acc, result) => {
  //  return acc + result.busy_calls;
  //}, 0);

  const busyCalls = results.at(-1).busy_calls;

  //const abandonedCalls = currentYearData.reduce((acc, result) => {
  //  return acc + result.abandoned_calls;
  //}, 0);

  const abandonedCalls = results.at(-1).abandoned_calls;

  //const nonBookableProspects = currentYearData.reduce((acc, result) => {
  //  return acc + (result.prospect_calls - result.bookable_calls);
  //}, 0);

  //const prospectCalls = currentYearData.reduce((acc, result) => {
  //  return acc + result.prospect_calls;
  //}, 0);

  const prospectCalls = results.at(-1).prospect_calls;

  //const connectedCalls = currentYearData.reduce((acc, result) => {
  //  return acc + result.connected_calls;
  //}, 0);

  const connectedCalls = results.at(-1).connected_calls;

  const bookableCalls = results.at(-1).bookable_calls;

  const nonBookableProspects = prospectCalls - bookableCalls;

  //const leadscoreCalls = currentYearData.reduce((acc, result) => {
  //  return acc + result.leadscore_calls;
  //}, 0);

  const leadscoreCalls = results.at(-1).leadscore_calls;

  const nonExaminedCalls = connectedCalls - leadscoreCalls;

  const prospectRate = Math.round((prospectCalls / leadscoreCalls) * 100) || 0;

  let prospectRateIndustry = 0;

  if (industry) {
    const currentYearDataIndustry = industry.filter((result) =>
      result.start_date.startsWith(currentYear)
    );

    const prospectCallsIndustry = currentYearDataIndustry.reduce((acc, result) => {
      return acc + result.prospect_calls;
    }, 0);

    const leadscoreCallsIndustry = currentYearDataIndustry.reduce((acc, result) => {
      return acc + result.leadscore_calls;
    }, 0);

    prospectRateIndustry = Math.round((prospectCallsIndustry / leadscoreCallsIndustry) * 100);
  }

  const prospectRateBarChartOptions = {
    chart: {
      type: 'bar',
      height: 100,
    },
    plotOptions: {
      series: {
        pointPadding: 0,
        borderWidth: 0,
      },
    },
    legend: {
      layout: 'vertical',
      align: 'left',
      verticalAlign: 'middle',
    },
    xAxis: {
      labels: { enabled: false },
      type: 'category',
    },
    yAxis: {
      title: { text: undefined },
      labels: {
        format: '{value}%',
      },
    },
    series: [
      {
        name: '6 Month Avg - Peers',
        data: [prospectRates.sixMonthAvgProspectRateIndustry],
        color: '#fbbf24',
      },
      {
        name: '6 Month Avg - You',
        data: [prospectRates.sixMonthAvgProspectRateYou],
        color: '#0ea5e9',
      },
    ],
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    title: { text: undefined },
    tooltip: {
      formatter() {
        return `${this.y}%`;
      },
    },
  };

  return (
    <div>
      <div className='row performance_summary_call_analysis'>
        <div className='col-sm-4'>
          <dl className='row'>
            <dt className='col-xs-9'>
              <strong>Bookable Prospects</strong>
            </dt>
            <dd className='col-xs-3'>
              <strong>
                {Common.formatNumberWithCommas(
                  missedOppSales + missedOppService + apptSales + apptService
                )}
              </strong>
            </dd>
            <dt className='col-xs-offset-2 col-xs-7'>Missed Opportunities</dt>
            <dd className='col-xs-3'>
              {Common.formatNumberWithCommas(missedOppSales + missedOppService)}
            </dd>
            <dt className='col-xs-offset-4 col-xs-5'>Sales/Prospects</dt>
            <dd className='col-xs-3'>{Common.formatNumberWithCommas(missedOppSales)}</dd>
            <dt className='col-xs-offset-4 col-xs-5'>Service</dt>
            <dd className='col-xs-3'>{Common.formatNumberWithCommas(missedOppService)}</dd>
            <dt className='col-xs-offset-2 col-xs-7' style={{ marginTop: 12 }}>
              Appointments
            </dt>
            <dd className='col-xs-3' style={{ marginTop: 12 }}>
              <strong>{Common.formatNumberWithCommas(apptSales + apptService)}</strong>
            </dd>
            <dt className='col-xs-offset-4 col-xs-5'>Sales/Prospects</dt>
            <dd className='col-xs-3'>{Common.formatNumberWithCommas(apptSales)}</dd>
            <dt className='col-xs-offset-4 col-xs-5'>Service</dt>
            <dd className='col-xs-3'>{Common.formatNumberWithCommas(apptService)}</dd>
            <dt className='col-xs-9' style={{ marginTop: 12 }}>
              <strong>Non-Bookable Prospects</strong>
            </dt>
            <dd className='col-xs-3' style={{ marginTop: 12 }}>
              <strong>{Common.formatNumberWithCommas(nonBookableProspects)}</strong>
            </dd>
          </dl>
        </div>
        <div className='col-sm-4'>
          <dl className='row'>
            <dt className='col-xs-9'>
              <strong>Non Prospect Calls</strong>
            </dt>
            <dd className='col-xs-3'>
              <strong>{Common.formatNumberWithCommas(nonProspectCalls)}</strong>
            </dd>
            <dt className='col-xs-offset-2 col-xs-7'>General Business</dt>
            <dd className='col-xs-3'>
              {Common.formatNumberWithCommas(callTypeMap['General Business']?.total_calls ?? 0)}
            </dd>
            <dt className='col-xs-offset-2 col-xs-7'>Current Customer</dt>
            <dd className='col-xs-3'>
              {Common.formatNumberWithCommas(callTypeMap['Current Customer']?.total_calls ?? 0)}
            </dd>
            <dt className='col-xs-9' style={{ marginTop: 12 }}>
              <strong>Non Qualifying Calls</strong>
            </dt>
            <dd className='col-xs-3' style={{ marginTop: 12 }}>
              <strong>
                {Common.formatNumberWithCommas(
                  nonExaminedCalls + noAnswerCalls + busyCalls + abandonedCalls
                )}
              </strong>
            </dd>
            <dt className='col-xs-offset-2 col-xs-7'>Non-Examined Calls</dt>
            <dd className='col-xs-3'>{Common.formatNumberWithCommas(nonExaminedCalls)}</dd>
            <dt className='col-xs-offset-2 col-xs-7'>Busy</dt>
            <dd className='col-xs-3'>{Common.formatNumberWithCommas(busyCalls)}</dd>
            <dt className='col-xs-offset-2 col-xs-7'>Abandoned</dt>
            <dd className='col-xs-3'>{Common.formatNumberWithCommas(abandonedCalls)}</dd>
          </dl>
        </div>
        <div className='col-sm-4'>
          <dl className='row'>
            <dt className='col-xs-9'>
              <strong>Total Calls</strong>
            </dt>
            <dd className='col-xs-3'>
              <strong>{Common.formatNumberWithCommas(totalCalls)}</strong>
            </dd>
            <dt className='col-xs-9' style={{ marginTop: 12 }}>
              <strong>Prospect Rate</strong>
            </dt>
            <dd className='col-xs-3' style={{ marginTop: 12 }}>
              <strong>{prospectRate}%</strong>
            </dd>
            <dt className='col-xs-offset-2 col-xs-7'>Peer Average</dt>
            <dd className='col-xs-3'>{prospectRateIndustry}%</dd>
          </dl>
        </div>
      </div>
      <hr />
      <div className='row'>
        <div className='col-sm-6'>
          <h3 className='text-center'>Appointment Conversion Rate</h3>
          <table className='table table-condensed'>
            <thead>
              <tr>
                <th>Period</th>
                <th>Owner</th>
                <th>Peers</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Current Month</td>
                <td>{conversionRates.currentMonthConversionRateYou}%</td>
                <td>{conversionRates.currentMonthConversionRateIndustry}%</td>
              </tr>
              <tr>
                <td>Prior Month</td>
                <td>{conversionRates.priorMonthConversionRateYou}%</td>
                <td>{conversionRates.priorMonthConversionRateIndustry}%</td>
              </tr>
              <tr>
                <td>6 Month Avg</td>
                <td>{conversionRates.sixMonthAverageConversionYou}%</td>
                <td>{conversionRates.sixMonthAverageConversionIndustry}%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className='col-sm-6'>
          <h3 className='text-center'>Prospect Rate</h3>
          <div style={{ border: '1px solid #d1d5db' }}>
            <HighchartsReact highcharts={Highcharts} options={prospectRateBarChartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
}

CallAnalysis.propTypes = {
  data: PropTypes.object.isRequired,
  conversionRates: PropTypes.object.isRequired,
  prospectRates: PropTypes.object.isRequired,
  results: PropTypes.array.isRequired,
  industry: PropTypes.array,
  currentYear: PropTypes.string.isRequired,
};

export default CallAnalysis;
